import * as M from "@discovery/prelude/lib/data/maybe";
import { Typography } from "@discovery/theming";
import { Details, Paragraph } from "../../atoms/body-text";
import { SmartLink } from "../../atoms/smart-link";
import * as styles from "./styles.css";
const isType = (type) => (x) => x.type === type;
const takeWhile = (p) => (xs) => {
    let i = 0;
    const l = xs.length;
    while (i < l && p(xs[i])) {
        i++;
    }
    return [xs.slice(0, i), xs.slice(i, l)];
};
const filterInRange = (xs, offset) => xs.filter((x) => offset >= x.offset && offset < x.offset + x.length);
const getTextDescriptions = (block) => {
    const { nodes, prev } = block.text.split("").reduce(({ nodes, prev, }, c, i) => {
        const styles = filterInRange(block.inlineStyleRanges, i).map((x) => x.style);
        const keys = filterInRange(block.entityRanges, i).map((x) => x.key);
        const same = prev !== null &&
            styles.length === prev.styles.length &&
            styles.every((s) => prev.styles.includes(s)) &&
            keys.length === prev.keys.length &&
            keys.every((n) => prev.keys.includes(n));
        return {
            nodes: prev === null ? nodes : same ? nodes : nodes.concat([prev]),
            prev: {
                keys,
                styles,
                text: prev === null ? c : same ? prev.text + c : c,
            },
        };
    }, {
        nodes: [],
        prev: null,
    });
    return prev === null ? nodes : nodes.concat([prev]).filter((x) => x !== null);
};
const LinkEntityBlock = (props) => (React.createElement(SmartLink, { keyM: M.of(props.key), link: { kind: "external", to: props.entity.data.href } },
    React.createElement(React.Fragment, null, props.children)));
const entityTypeMap = {
    LINK: LinkEntityBlock,
};
function createMarkup(text) {
    return { __html: text };
}
const getTextNodes = (entityMap, block) => getTextDescriptions(block).map((x, i) => x.keys.reduce((comp, n) => {
    const entity = entityMap[n];
    return entityTypeMap[entity.type]({
        children: comp,
        entity,
        key: block.key + i,
    });
}, React.createElement(Details, { key: i, textStyles: [
        x.styles.includes("BOLD") ? [Typography.TextStyles.BOLD] : [],
        x.styles.includes("ITALIC") ? [Typography.TextStyles.ITALIC] : [],
        x.styles.includes("UNDERLINE")
            ? [Typography.TextStyles.UNDERLINE]
            : [],
    ].flat() },
    React.createElement("div", { dangerouslySetInnerHTML: createMarkup(x.text) }))));
const takeUnstyled = takeWhile(isType("unstyled"));
const TextBlock = (entityMap, blocks) => {
    const [xs, ys] = takeUnstyled(blocks);
    return [
        React.createElement("div", { key: blocks[0] && blocks[0].key }, xs.map((x) => (React.createElement(Paragraph, { key: x.key }, getTextNodes(entityMap, x))))),
        ys,
    ];
};
const takeHeader = (k) => takeWhile(isType(`header-${k}`));
const Header = (HComp, k) => (entityMap, blocks) => {
    const [xs, ys] = takeHeader(k)(blocks);
    return [
        React.createElement("div", { key: blocks[0] && blocks[0].key }, xs.map((x) => (React.createElement(HComp, null, getTextNodes(entityMap, x))))),
        ys,
    ];
};
const ListItem = (entityMap) => (block) => (React.createElement("li", { key: block.key },
    React.createElement(Paragraph, { className: styles.listItem }, getTextNodes(entityMap, block))));
const takeOrderedListItem = takeWhile(isType("ordered-list-item"));
const OrderedListItem = (entityMap, blocks) => {
    const [xs, ys] = takeOrderedListItem(blocks);
    return [
        React.createElement("ol", { key: blocks[0] && blocks[0].key }, xs.map(ListItem(entityMap))),
        ys,
    ];
};
const takeUnorderedListItem = takeWhile(isType("unordered-list-item"));
const UnorderedListItem = (entityMap, blocks) => {
    const [xs, ys] = takeUnorderedListItem(blocks);
    return [
        React.createElement("ul", { key: blocks[0] && blocks[0].key }, xs.map(ListItem(entityMap))),
        ys,
    ];
};
const takeBlockQuote = takeWhile(isType("blockquote"));
const BlockQuote = (entityMap, blocks) => {
    const [xs, ys] = takeBlockQuote(blocks);
    return [
        React.createElement("blockquote", null, xs.map((block) => getTextNodes(entityMap, block))),
        ys,
    ];
};
const NotSupported = (type) => (blocks) => {
    const a = takeWhile(isType(type))(blocks);
    return [React.createElement(React.Fragment, null), a[1]];
};
const blockTypeMap = (blocks, entityMap) => {
    const type = blocks[0] && blocks[0].type;
    switch (type) {
        case "unstyled":
            return TextBlock(entityMap, blocks);
        case "header-one":
            return Header(Typography.H1, "one")(entityMap, blocks);
        case "header-two":
            return Header(Typography.H2, "two")(entityMap, blocks);
        case "header-three":
            return Header(Typography.H3, "three")(entityMap, blocks);
        case "header-four":
            return Header(Typography.H4, "four")(entityMap, blocks);
        case "header-five":
            return Header(Typography.H5, "five")(entityMap, blocks);
        case "header-six":
            return Header(Typography.H6, "six")(entityMap, blocks);
        case "ordered-list-item":
            return OrderedListItem(entityMap, blocks);
        case "unordered-list-item":
            return UnorderedListItem(entityMap, blocks);
        case "blockquote":
            return BlockQuote(entityMap, blocks);
        default:
            return NotSupported(type)(blocks);
    }
};
const renderAST = (ast) => {
    let blocks = ast.blocks;
    const vdom = [];
    while (blocks.length > 0) {
        const [vdom_, blocks_] = blockTypeMap(blocks, ast.entityMap);
        vdom.push(vdom_);
        blocks = blocks_;
    }
    return vdom;
};
export const RenderBlocks = ({ ast }) => (React.createElement("div", { className: styles.container }, renderAST(ast)));

